/**
 * Builds a URL pointing at the shared self-hosted imgproxy instance for
 * resizing/cropping an image. Replaces the previous imaginary builder.
 *
 * imaginary was retired because it could neither read nor write AVIF (HTTP 415
 * on AVIF input, 400 on AVIF output) and upstream had no release after v1.2.4
 * in August 2020. Old imaginary URLs keep working — imaginary.hernas.com now
 * resolves to a shim that 301s them here — so this only removes a redirect.
 *
 * @param imageProcessingURL - Base URL of the imgproxy service (incl. trailing
 *   slash), e.g. `https://imgproxy.hernas.com/`.
 * @param sourceURL - Absolute URL imgproxy should fetch the original image
 *   from. Must be on the imgproxy `IMGPROXY_ALLOWED_SOURCES` list.
 * @param opts - Sizing / format / cropping options.
 */
export interface ImgproxyOpts {
  width?: number;
  height?: number;
  quality?: number;
  /** Crop to square (uses width as both dimensions, smart gravity). */
  makeSquareFit?: boolean;
  /** Force JPEG output. Otherwise imgproxy negotiates on the Accept header. */
  forceJPEG?: boolean;
  /** Optional override of gravity. Defaults to smart. */
  gravity?: string;
}

export function buildImgproxyUrl(imageProcessingURL: string, sourceURL: string, opts: ImgproxyOpts = {}): string {
  const { width, height, quality, makeSquareFit, forceJPEG, gravity = 'smart' } = opts;
  const finalWidth = width !== undefined ? Math.min(1920, width) : undefined;
  const finalHeight = height !== undefined ? Math.min(1920, height) : undefined;

  // `fill` is cover-crop, `fit` preserves aspect — the same split imaginary
  // made between its crop and resize endpoints. Neither enlarges past the
  // source size, so this stays a like-for-like swap.
  const squareSide = finalHeight ?? finalWidth;
  const resize =
    makeSquareFit === true && squareSide !== undefined
      ? `rs:fill:${finalWidth ?? squareSide}:${squareSide}`
      : `rs:fit:${finalWidth ?? 0}:${finalHeight ?? 0}`;

  const options = [resize, `g:${gravity === 'smart' ? 'sm' : gravity}`, `q:${quality ?? 85}`];

  // No extension means imgproxy negotiates via the Accept header, which is
  // what imaginary's `type=auto` did. An explicit jpeg stays pinned.
  const extension = forceJPEG === true ? '@jpg' : '';

  return `${imageProcessingURL}insecure/${options.join('/')}/plain/${encodeURIComponent(sourceURL)}${extension}`;
}
